import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, CircularProgress,
    Input, Select, Option, IconButton, Avatar, Tooltip,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import ArrowBackRoundedIcon        from '@mui/icons-material/ArrowBackRounded';
import HistoryRoundedIcon          from '@mui/icons-material/HistoryRounded';
import FilterListRoundedIcon       from '@mui/icons-material/FilterListRounded';
import SearchRoundedIcon           from '@mui/icons-material/SearchRounded';
import ExpandMoreRoundedIcon       from '@mui/icons-material/ExpandMoreRounded';
import VisibilityRoundedIcon       from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon    from '@mui/icons-material/VisibilityOffRounded';
import WarningAmberRoundedIcon     from '@mui/icons-material/WarningAmberRounded';
import ErrorRoundedIcon            from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon             from '@mui/icons-material/InfoRounded';
import RefreshRoundedIcon          from '@mui/icons-material/RefreshRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIGetEvents, APIMarkEventSeen, APIGetEmployees, APIMarkAllEventsSeen,
    APIGetEventsUnseenCount,
} from '../../api';
import MemeLoader from '../../components/MemeLoader';

// ─── Утилиты ──────────────────────────────────────────────────────────────────
const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const getMonthStart = () => {
    const now = new Date();
    return toISO(new Date(now.getFullYear(), now.getMonth(), 1));
};

const fmtDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('ru-RU', {
        weekday: 'short', day: 'numeric', month: 'short',
    });
};

const fmtTime = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleTimeString('ru-RU', {
        hour: '2-digit', minute: '2-digit',
    });
};

const getInitials = (name = '') => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase() || '?';
};

// ─── Карта стилей для уровней важности ───────────────────────────────────────
const LEVEL_META = {
    info: {
        label: 'Инфо',
        color: 'var(--joy-palette-text-tertiary)',
        bg: 'rgba(154,154,184,0.10)',
        accent: 'var(--joy-palette-text-tertiary)',
        icon: <InfoRoundedIcon sx={{ fontSize: 12 }} />,
    },
    important: {
        label: 'Важно',
        color: '#f59e0b',
        bg: 'rgba(245,158,11,0.10)',
        accent: '#f59e0b',
        icon: <WarningAmberRoundedIcon sx={{ fontSize: 12 }} />,
    },
    critical: {
        label: 'Критично',
        color: '#ef4444',
        bg: 'rgba(239,68,68,0.12)',
        accent: '#ef4444',
        icon: <ErrorRoundedIcon sx={{ fontSize: 12 }} />,
    },
};

// ─── Карта цветов для действий ────────────────────────────────────────────────
const ACTION_COLORS = {
    create:    { color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
    update:    { color: '#3b82f6', bg: 'rgba(59,130,246,0.12)' },
    delete:    { color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
    approve:   { color: '#22c55e', bg: 'rgba(34,197,94,0.12)' },
    reject:    { color: '#ef4444', bg: 'rgba(239,68,68,0.12)' },
    cancel:    { color: '#f59e0b', bg: 'rgba(245,158,11,0.12)' },
    broadcast: { color: 'var(--joy-palette-primary-400)', bg: 'rgba(162,38,226,0.12)' },
};

// ─── Возможные типы объектов (для фильтра) ───────────────────────────────────
const TARGET_TYPES = [
    { value: '',                   label: 'Все типы' },
    { value: 'order',              label: 'Чеки' },
    { value: 'fine',               label: 'Штрафы' },
    { value: 'reward',             label: 'Премии' },
    { value: 'admin_salary',       label: 'Оклады админов' },
    { value: 'employee',           label: 'Сотрудники' },
    { value: 'role',               label: 'Роли' },
    { value: 'location',           label: 'Точки' },
    { value: 'message',            label: 'Сообщения' },
    { value: 'assignment',         label: 'Назначения' },
    { value: 'change_request',     label: 'Запросы на изм.' },
    { value: 'fill_request',       label: 'Заявки на заполнение' },
    { value: 'substitution',       label: 'Замены' },
    { value: 'sale_notification',  label: 'Уведомл. о продаже' },
    { value: 'availability',       label: 'Доступность' },
    { value: 'shift',              label: 'Смены workstation' },
    { value: 'month',              label: 'Финансовый месяц' },
];

// ─── Анимации (как в OrdersPage) ──────────────────────────────────────────────
const pageVariants = {
    initial: { opacity: 0, y: 16 },
    animate: { opacity: 1, y: 0, transition: { duration: 0.38, ease: [0.22, 1, 0.36, 1] } },
    exit:    { opacity: 0, y: -12, transition: { duration: 0.22 } },
};
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.04 } } };
const fadeUp  = {
    hidden:  { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } },
};

const PAGE_SIZE = 50;   // должно соответствовать настройке list_events на бэке

// ══════════════════════════════════════════════════════════════════
//  Карточка события
// ══════════════════════════════════════════════════════════════════
function EventCard({ event, onToggleExpand, expanded, onMarkSeen, seenUntil }) {
    const level   = LEVEL_META[event.level] || LEVEL_META.info;
    const action  = ACTION_COLORS[event.action] || ACTION_COLORS.update;

    // «Прочитано» = событие создано не позже, чем seenUntil пользователя.
    // event.seen приходит с сервера, но на всякий случай fallback.
    const seen = event.seen ?? (
        seenUntil ? new Date(event.date_create) <= new Date(seenUntil) : false
    );

    // При раскрытии отмечаем как просмотренное
    const handleClick = () => {
        if (!seen) onMarkSeen(event.id);
        onToggleExpand(event.id);
    };

    const hasPayload = event.payload && Object.keys(event.payload).length > 0;

    return (
        <Box
            onClick={handleClick}
            sx={{
                display: 'flex', flexDirection: 'column',
                gap: 1,
                px: 2, py: 1.2,
                borderLeft: '3px solid',
                borderLeftColor: seen ? 'transparent' : level.accent,
                bgcolor: seen
                    ? 'transparent'
                    : `${level.bg}`,
                opacity: seen ? 0.78 : 1,
                cursor: 'pointer',
                transition: 'background .15s, opacity .15s',
                '&:hover': { bgcolor: 'var(--joy-palette-background-level2)' },
            }}
        >
            {/* Основная строка */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                {/* Время */}
                <Typography sx={{
                    fontSize: 11, color: 'var(--joy-palette-text-tertiary)',
                    fontVariantNumeric: 'tabular-nums',
                    flexShrink: 0, minWidth: 38,
                }}>
                    {fmtTime(event.date_create)}
                </Typography>

                {/* Аватар того, кто сделал */}
                <Avatar src={event.actor_avatar_url} size="sm" sx={{
                    width: 30, height: 30, fontSize: 10, fontWeight: 700,
                    bgcolor: 'rgba(162,38,226,0.15)',
                    color: 'var(--joy-palette-primary-300)',
                    border: '1.5px solid var(--joy-palette-primary-500)',
                    flexShrink: 0,
                }}>
                    {getInitials(event.actor_name)}
                </Avatar>

                {/* Описание */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
                        <Typography sx={{
                            fontSize: 13, fontWeight: 700,
                            color: 'var(--joy-palette-text-primary)',
                        }}>
                            {event.actor_name || 'Система'}
                        </Typography>
                        <Chip size="sm" variant="soft" sx={{
                            fontSize: 10, fontWeight: 700, height: 18,
                            color: action.color, bgcolor: action.bg,
                            border: 'none',
                        }}>
                            {event.action_display}
                        </Chip>
                        <Typography sx={{
                            fontSize: 11, color: 'var(--joy-palette-text-tertiary)',
                            fontWeight: 600,
                        }}>
                            {event.target_display?.toLowerCase()}
                        </Typography>
                    </Box>
                    <Typography sx={{
                        fontSize: 12, color: 'var(--joy-palette-text-secondary)', mt: 0.3,
                        wordBreak: 'break-word',
                    }}>
                        {event.target_repr}
                    </Typography>
                </Box>

                {/* Уровень */}
                <Chip size="sm" variant="soft" sx={{
                    fontSize: 10, fontWeight: 700, height: 20,
                    color: level.color, bgcolor: level.bg,
                    border: 'none', flexShrink: 0,
                }}
                    startDecorator={level.icon}
                >
                    {level.label}
                </Chip>

                {/* Иконка раскрытия — только если есть payload */}
                {hasPayload && (
                    <ExpandMoreRoundedIcon
                        sx={{
                            fontSize: 18, color: 'var(--joy-palette-text-tertiary)',
                            transition: 'transform .2s',
                            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                            flexShrink: 0,
                        }}
                    />
                )}
            </Box>

            {/* Раскрывающийся payload */}
            <AnimatePresence>
                {expanded && hasPayload && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22 }}
                        style={{ overflow: 'hidden' }}
                    >
                        <Box sx={{
                            mt: 0.5, ml: { xs: 0, sm: 6.5 },
                            p: 1.5,
                            borderRadius: 'var(--joy-radius-md)',
                            border: '1px solid var(--joy-palette-divider)',
                            bgcolor: 'var(--joy-palette-background-level2)',
                            fontFamily: 'var(--joy-fontFamily-code)',
                            fontSize: 11, color: 'var(--joy-palette-text-secondary)',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-all',
                            overflowX: 'auto',
                        }}>
                            {JSON.stringify(event.payload, null, 2)}
                        </Box>
                    </motion.div>
                )}
            </AnimatePresence>
        </Box>
    );
}

// ══════════════════════════════════════════════════════════════════
//  Основная страница
// ══════════════════════════════════════════════════════════════════
export default function HistoryPage() {
    const navigate = useNavigate();
    const { token, hasPerm, showToast, onRefreshTick, triggerRefresh, account } = useContext(AppContext);

    // ── Данные ────────────────────────────────────────────────────
    const [events, setEvents]       = useState([]);
    const [total, setTotal]         = useState(0);
    const [page, setPage]           = useState(1);
    const [loading, setLoading]     = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [expandedId, setExpandedId] = useState(null);

    // ── Фильтры ───────────────────────────────────────────────────
    const [search,        setSearch]        = useState('');
    const [showFilters,   setShowFilters]   = useState(false);
    const [dateFrom,      setDateFrom]      = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - 30);
        return toISO(d);
    });
    const [dateTo,        setDateTo]        = useState(toISO(new Date()));
    const [filterActor,   setFilterActor]   = useState('');
    const [filterTarget,  setFilterTarget]  = useState('');
    const [filterLevel,   setFilterLevel]   = useState('');

    // ── Вкладка-фильтр: 'unseen' | 'all' | 'critical' | 'important' ───
    // По умолчанию открываем «Непрочитанные»
    const [activeTab,     setActiveTab]     = useState('unseen');

    // ── Серверный счётчик непрочитанных (с разбивкой по level) ────
    const [unseenStats,   setUnseenStats]   = useState({
        total: 0, critical: 0, important: 0, info: 0,
    });

    // ── Защита от лишних запросов при изменении фильтров ─────────
    const filtersKey = useMemo(() => JSON.stringify({
        dateFrom, dateTo, filterActor, filterTarget, filterLevel, activeTab,
    }), [dateFrom, dateTo, filterActor, filterTarget, filterLevel, activeTab]);

    // ── Сборка params для API ─────────────────────────────────────
    //   activeTab=unseen   → ?unseen_only=1
    //   activeTab=critical → ?level=critical (перетирает filterLevel из «расширенных»)
    //   activeTab=important→ ?level=important
    //   activeTab=all      → без фильтра
    const buildParams = useCallback((pageNum) => {
        const p = { page: pageNum };
        if (dateFrom)     p.date_from    = dateFrom;
        if (dateTo)       p.date_to      = dateTo;
        if (filterActor)  p.actor_id     = filterActor;
        if (filterTarget) p.target_type  = filterTarget;

        if (activeTab === 'unseen') {
            p.unseen_only = 1;
            if (filterLevel) p.level = filterLevel;   // можно фильтровать непрочитанные по уровню
        } else if (activeTab === 'critical') {
            p.level = 'critical';
        } else if (activeTab === 'important') {
            p.level = 'important';
        } else {
            // 'all'
            if (filterLevel) p.level = filterLevel;
        }
        return p;
    }, [dateFrom, dateTo, filterActor, filterTarget, filterLevel, activeTab]);

    // ── Загрузка первой страницы при изменении фильтров ──────────
    const loadEvents = useCallback(async () => {
        if (!token) return false;
        setLoading(true);
        setExpandedId(null);
        const data = await APIGetEvents(token, buildParams(1));
        if (data && Array.isArray(data.events)) {
            setEvents(data.events);
            setTotal(data.total || 0);
            setPage(1);
        }
        setLoading(false);
        return true;
    }, [token, buildParams]);

    // ── Подгрузка следующей страницы ──────────────────────────────
    const loadMore = useCallback(async () => {
        if (!token || loadingMore) return;
        const nextPage = page + 1;
        setLoadingMore(true);
        const data = await APIGetEvents(token, buildParams(nextPage));
        if (data && Array.isArray(data.events)) {
            setEvents(prev => [...prev, ...data.events]);
            setPage(nextPage);
        }
        setLoadingMore(false);
    }, [token, page, loadingMore, buildParams]);

    // ── Список сотрудников для фильтра «Кто» ──────────────────────
    useEffect(() => {
        if (!token) return;
        APIGetEmployees(token).then(list => {
            if (Array.isArray(list)) setEmployees(list);
        });
    }, [token]);

    // ── Перезагрузка при смене фильтров ───────────────────────────
    useEffect(() => { loadEvents(); }, [filtersKey]); // eslint-disable-line

    // ── Подписка на глобальное обновление ─────────────────────────
    useEffect(() => onRefreshTick(loadEvents), [onRefreshTick, loadEvents]);

    // ── Подгрузка серверного счётчика непрочитанных ───────────────
    const loadUnseenStats = useCallback(async () => {
        if (!token) return;
        const stats = await APIGetEventsUnseenCount(token);
        setUnseenStats({
            total:     stats?.total     ?? stats?.count ?? 0,
            critical:  stats?.critical  ?? 0,
            important: stats?.important ?? 0,
            info:      stats?.info      ?? 0,
        });
    }, [token]);

    useEffect(() => { loadUnseenStats(); }, [loadUnseenStats]);

    // ── При выходе со страницы: «отметить всё прочитанным» ────────
    // Раньше вызывалось при ВХОДЕ — это сбрасывало счётчик до того, как пользователь
    // увидел события. Теперь отмечаем только когда пользователь уходит со страницы.
    useEffect(() => {
        return () => {
            if (!token) return;
            APIMarkAllEventsSeen(token).then(() => {
                if (typeof triggerRefresh === 'function') triggerRefresh();
            });
        };
        // eslint-disable-next-line
    }, [token]);

    // ── Отметить событие как просмотренное ────────────────────────
    const handleMarkSeen = useCallback(async (eventId) => {
        // Оптимистично обновим в UI
        setEvents(prev => prev.map(e => e.id === eventId ? { ...e, seen: true } : e));
        await APIMarkEventSeen(token, eventId);
        // Подтягиваем актуальный серверный счётчик
        loadUnseenStats();
    }, [token, loadUnseenStats]);

    const handleToggleExpand = useCallback((eventId) => {
        setExpandedId(prev => prev === eventId ? null : eventId);
    }, []);

    // ── Локальный поиск по target_repr / actor_name ──────────────
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return events;
        return events.filter(e =>
            (e.target_repr || '').toLowerCase().includes(q) ||
            (e.actor_name  || '').toLowerCase().includes(q)
        );
    }, [events, search]);

    // ── Группировка по дате ───────────────────────────────────────
    const grouped = useMemo(() => {
        const map = {};
        for (const ev of filtered) {
            const key = (ev.date_create || '').slice(0, 10) || 'unknown';
            if (!map[key]) map[key] = [];
            map[key].push(ev);
        }
        // Дни по убыванию даты (внутри сервер уже отсортировал по -date_create)
        return Object.entries(map).sort((a, b) => b[0].localeCompare(a[0]));
    }, [filtered]);

    // ── Счётчики для верхних карточек (берём с сервера) ──────────
    const unseenCount   = unseenStats.total;
    const criticalCount = unseenStats.critical;
    const importantCount = unseenStats.important;

    // ── Если нет права просмотра — заглушка ──────────────────────
    if (!hasPerm('events.view')) {
        return (
            <Box sx={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}>
                <Header />
                <Box sx={{ maxWidth: 600, mx: 'auto', textAlign: 'center', py: 8, px: 3 }}>
                    <HistoryRoundedIcon sx={{ fontSize: 48, color: 'var(--joy-palette-text-tertiary)', mb: 1.5 }} />
                    <Typography sx={{ fontSize: 16, color: 'var(--joy-palette-text-secondary)' }}>
                        У вас нет прав на просмотр журнала событий
                    </Typography>
                </Box>
            </Box>
        );
    }

    const hasMore = events.length < total;

    return (
        <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit"
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />

            <Box sx={{ maxWidth: 900, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* ── Шапка ──────────────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.04 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <IconButton variant="plain" onClick={() => navigate('/manager')}
                                sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                                <ArrowBackRoundedIcon />
                            </IconButton>
                            <Box>
                                <Typography sx={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', color: 'var(--joy-palette-text-primary)' }}>
                                    История действий
                                </Typography>
                                <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                                    Все изменения сотрудников за выбранный период
                                </Typography>
                            </Box>
                        </Box>
                        <Tooltip title="Обновить" size="sm">
                            <IconButton
                                color="primary"
                                variant="soft"
                                onClick={loadEvents}
                                disabled={loading}
                                // sx={{
                                //     bgcolor: 'var(--joy-palette-background-level2)',
                                //     border: '1px solid var(--joy-palette-divider)',
                                //     color: 'white',
                                //     '&:hover': { bgcolor: 'var(--joy-palette-background-level3)' },
                                // }}
                            >
                                <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </motion.div>

                {/* ── Итого-карточки (кликабельные → переключают вкладку) ── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.08 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(4,1fr)' }, gap: 1.5, mb: 2.5 }}>
                        {[
                            { tab: 'all',       label: 'Всего',         value: total,           color: 'var(--joy-palette-primary-400)', bg: 'rgba(162,38,226,0.08)', icon: <HistoryRoundedIcon sx={{ fontSize: 18 }} /> },
                            { tab: 'unseen',    label: 'Непрочитано',   value: unseenCount,     color: '#3b82f6',                         bg: 'rgba(59,130,246,0.08)', icon: <VisibilityOffRoundedIcon sx={{ fontSize: 18 }} /> },
                            { tab: 'critical',  label: 'Критичных',     value: criticalCount,   color: '#ef4444',                         bg: 'rgba(239,68,68,0.08)',  icon: <ErrorRoundedIcon sx={{ fontSize: 18 }} /> },
                            { tab: 'important', label: 'Важных',        value: importantCount,  color: '#f59e0b',                         bg: 'rgba(245,158,11,0.08)', icon: <WarningAmberRoundedIcon sx={{ fontSize: 18 }} /> },
                        ].map(s => {
                            const isActive = activeTab === s.tab;
                            return (
                                <Box key={s.label}
                                    onClick={() => setActiveTab(s.tab)}
                                    sx={{
                                        px: 2, py: 1.8,
                                        borderRadius: 'var(--joy-radius-lg)',
                                        border: '1px solid',
                                        borderColor: isActive ? s.color : 'var(--joy-palette-divider)',
                                        bgcolor: s.bg,
                                        cursor: 'pointer',
                                        transition: 'all .15s',
                                        boxShadow: isActive ? `0 0 0 2px ${s.color}33` : 'none',
                                        '&:hover': { borderColor: s.color },
                                    }}
                                >
                                    <Box sx={{ color: s.color, mb: 0.5 }}>{s.icon}</Box>
                                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</Typography>
                                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-secondary)', mt: 0.4 }}>{s.label}</Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </motion.div>

                {/* ── Фильтры ──────────────────────────────────── */}
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.12 }}>
                    <Box sx={{
                        bgcolor: 'var(--joy-palette-background-level1)',
                        border: '1px solid var(--joy-palette-divider)',
                        borderRadius: 'var(--joy-radius-xl)',
                        p: { xs: 1.5, sm: 2 }, mb: 2.5,
                    }}>
                        {/* Строка поиска + кнопка фильтров */}
                        <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                            <Input
                                placeholder="Поиск по описанию, по сотруднику…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                startDecorator={<SearchRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-text-tertiary)' }} />}
                                size="sm"
                            />
                            <IconButton
                                variant={showFilters ? 'solid' : 'outlined'}
                                color={showFilters ? 'primary' : 'neutral'}
                                onClick={() => setShowFilters(p => !p)}
                                size="sm"
                                sx={{
                                    bgcolor: 'var(--joy-palette-background-level2)',
                                    border: '1px solid var(--joy-palette-divider)',
                                    '&:hover': { bgcolor: 'var(--joy-palette-background-level3)' },
                                }}
                            >
                                <FilterListRoundedIcon sx={{ fontSize: 18, color: 'white' }} />
                            </IconButton>
                        </Box>

                        {/* Быстрые периоды */}
                        <Box sx={{ display: 'flex', gap: 0.8, flexWrap: 'wrap', mb: 1.5 }}>
                            {[
                                { label: 'Сегодня', from: toISO(new Date()), to: toISO(new Date()) },
                                { label: 'Вчера',
                                    from: (() => { const d = new Date(); d.setDate(d.getDate()-1); return toISO(d); })(),
                                    to:   (() => { const d = new Date(); d.setDate(d.getDate()-1); return toISO(d); })() },
                                { label: 'Неделя',
                                    from: (() => { const d = new Date(); d.setDate(d.getDate() - (d.getDay() === 0 ? 6 : d.getDay()-1)); return toISO(d); })(),
                                    to: toISO(new Date()) },
                                { label: 'Месяц',
                                    from: (() => { const d = new Date(); d.setDate(d.getDate()-30); return toISO(d); })(),
                                    to: toISO(new Date()) },
                                { label: 'Все',  from: '2000-01-01', to: toISO(new Date()) },
                            ].map(p => (
                                <Chip key={p.label} size="sm" variant="outlined" color="neutral"
                                    onClick={() => { setDateFrom(p.from); setDateTo(p.to); }}
                                    sx={{ cursor: 'pointer', fontWeight: 700, fontSize: 12, px: 2, py: 0.5,
                                        "&:hover": {
                                            color: 'black'
                                        }
                                    }}
                                >
                                    {p.label}
                                </Chip>
                            ))}
                        </Box>

                        {/* Развёрнутые фильтры */}
                        <AnimatePresence>
                            {showFilters && (
                                <motion.div
                                    initial={{ height: 0, opacity: 0 }}
                                    animate={{ height: 'auto', opacity: 1 }}
                                    exit={{ height: 0, opacity: 0 }}
                                    transition={{ duration: 0.25 }}
                                    style={{ overflow: 'hidden' }}
                                >
                                    <Box sx={{
                                        display: 'grid',
                                        gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(5, 1fr)' },
                                        gap: 1, pt: 1,
                                    }}>
                                        <Input type="date" size="sm" value={dateFrom}
                                            onChange={(e) => setDateFrom(e.target.value)}
                                            slotProps={{ input: { 'aria-label': 'Дата от' } }}
                                        />
                                        <Input type="date" size="sm" value={dateTo}
                                            onChange={(e) => setDateTo(e.target.value)}
                                            slotProps={{ input: { 'aria-label': 'Дата до' } }}
                                        />
                                        <Select size="sm" value={filterActor}
                                            onChange={(_, v) => setFilterActor(v || '')}
                                            placeholder="Кто"
                                        >
                                            <Option value="">Все сотрудники</Option>
                                            {employees.map(e => (
                                                <Option key={e.id} value={e.id}>
                                                    {e.first_name} {e.last_name}
                                                </Option>
                                            ))}
                                        </Select>
                                        <Select size="sm" value={filterTarget}
                                            onChange={(_, v) => setFilterTarget(v || '')}
                                            placeholder="Тип"
                                        >
                                            {TARGET_TYPES.map(t => (
                                                <Option key={t.value} value={t.value}>{t.label}</Option>
                                            ))}
                                        </Select>
                                        <Select size="sm" value={filterLevel}
                                            onChange={(_, v) => setFilterLevel(v || '')}
                                            placeholder="Важность"
                                        >
                                            <Option value="">Любая</Option>
                                            <Option value="info">Инфо</Option>
                                            <Option value="important">Важно</Option>
                                            <Option value="critical">Критично</Option>
                                        </Select>
                                    </Box>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </Box>
                </motion.div>

                {/* ── Список ───────────────────────────────────── */}
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <MemeLoader />
                    </Box>
                ) : filtered.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 8 }}>
                        <HistoryRoundedIcon sx={{ fontSize: 48, color: 'var(--joy-palette-text-tertiary)', mb: 1.5 }} />
                        <Typography sx={{ fontSize: 14, color: 'var(--joy-palette-text-tertiary)' }}>
                            Нет событий за выбранный период
                        </Typography>
                    </Box>
                ) : (
                    <motion.div variants={stagger} initial="hidden" animate="visible">
                        <Stack spacing={2}>
                            {grouped.map(([date, items]) => (
                                <motion.div key={date} variants={fadeUp}>
                                    <Box sx={{
                                        bgcolor: 'var(--joy-palette-background-level1)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        borderRadius: 'var(--joy-radius-xl)',
                                        overflow: 'hidden',
                                    }}>
                                        {/* Заголовок дня */}
                                        <Box sx={{
                                            px: 2, py: 1.2,
                                            borderBottom: '1px solid var(--joy-palette-divider)',
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                        }}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                {fmtDate(date)}
                                            </Typography>
                                            <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)' }}>
                                                {items.length} событ.
                                                {items.some(e => {
                                                    const seenUntil = account?.events_seen_until;
                                                    const seen = e.seen ?? (seenUntil ? new Date(e.date_create) <= new Date(seenUntil) : false);
                                                    return !seen;
                                                }) && (
                                                    <Box component="span" sx={{
                                                        ml: 1, color: '#3b82f6', fontWeight: 700,
                                                    }}>
                                                        · {items.filter(e => {
                                                            const seenUntil = account?.events_seen_until;
                                                            const seen = e.seen ?? (seenUntil ? new Date(e.date_create) <= new Date(seenUntil) : false);
                                                            return !seen;
                                                        }).length} нов.
                                                    </Box>
                                                )}
                                            </Typography>
                                        </Box>

                                        {/* События за день */}
                                        <Stack spacing={0} divider={
                                            <Box sx={{ height: '1px', bgcolor: 'var(--joy-palette-divider)' }} />
                                        }>
                                            {items.map(ev => (
                                                <EventCard
                                                    key={ev.id}
                                                    event={ev}
                                                    expanded={expandedId === ev.id}
                                                    onToggleExpand={handleToggleExpand}
                                                    onMarkSeen={handleMarkSeen}
                                                    seenUntil={account?.events_seen_until}
                                                />
                                            ))}
                                        </Stack>
                                    </Box>
                                </motion.div>
                            ))}
                        </Stack>

                        {/* «Показать ещё» */}
                        {hasMore && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2.5 }}>
                                <Button
                                    onClick={loadMore}
                                    disabled={loadingMore}
                                    variant="outlined"
                                    sx={{
                                        bgcolor: 'var(--joy-palette-background-level1)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        color: 'var(--joy-palette-text-primary)',
                                        fontSize: 13, fontWeight: 700,
                                        '&:hover': {
                                            bgcolor: 'var(--joy-palette-background-level2)',
                                            borderColor: 'var(--joy-palette-primary-500)',
                                        },
                                    }}
                                    startDecorator={loadingMore ? <CircularProgress size="sm" /> : null}
                                >
                                    {loadingMore
                                        ? 'Загрузка…'
                                        : `Показать ещё (${total - events.length} осталось)`}
                                </Button>
                            </Box>
                        )}
                    </motion.div>
                )}

            </Box>
        </motion.div>
    );
}